/**
 * InFeedAd Component
 * Full-row native advertisement that spans the entire grid width
 * Horizontal layout on desktop, stacked on mobile
 */

import React, { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useAdTracking } from "@/hooks/useAdTracking";
import type { Advertisement } from "@/types/advertising";

interface InFeedAdProps {
  ad: Advertisement;
  className?: string;
  variant?: "card" | "list";
}

const InFeedAd: React.FC<InFeedAdProps> = ({
  ad,
  className = "",
  variant = "card",
}) => {
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const { adRef, handleClick, impressionTracked } = useAdTracking({
    adId: ad.id,
    placement: ad.placement,
    targetUrl: ad.targetUrl,
  });

  const handleImageError = () => {
    console.error(`Failed to load in-feed ad image: ${ad.imageUrl}`);
    setImageError(true);
    setIsLoading(false);
  };

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const handleAdClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    handleClick(event);
  };

  if (!ad.isActive) {
    return null;
  }

  return (
    <div
      ref={adRef}
      className={`in-feed-ad ${className}`}
      data-ad-id={ad.id}
      data-tracked={impressionTracked}
      role="complementary"
      aria-label="Sponsored Content"
    >
      <Link
        href={ad.targetUrl}
        target="_blank"
        rel="noopener noreferrer sponsored"
        onClick={handleAdClick}
        className="block transition-transform hover:scale-[1.005] duration-200"
        aria-label={`Sponsored: ${ad.title}`}
      >
        <div className="w-full bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-200 border border-gray-100">
          <div className="flex flex-col md:flex-row">
            {/* Image: full width on mobile, left side on desktop */}
            {ad.imageUrl && !imageError && (
              <div className="relative w-full md:w-1/2 lg:w-2/5 aspect-[16/9] md:aspect-auto md:min-h-[180px] flex-shrink-0">
                {isLoading && (
                  <div className="absolute inset-0 bg-gray-200 animate-pulse" />
                )}
                <Image
                  src={ad.imageUrl}
                  alt={ad.altText || ad.title}
                  fill
                  className="object-cover"
                  onError={handleImageError}
                  onLoad={handleImageLoad}
                  sizes="(max-width: 768px) 100vw, 50vw"
                />
              </div>
            )}

            {/* Content: below image on mobile, right side on desktop */}
            <div className="flex-1 p-4 md:p-6 flex flex-col justify-center">
              <span className="text-xs font-semibold text-yellow-700 bg-yellow-100 px-2 py-1 rounded w-fit uppercase tracking-wide">
                Sponsored
              </span>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mt-2 line-clamp-2">
                {ad.title}
              </h3>
              {ad.description && (
                <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                  {ad.description}
                </p>
              )}
              <div className="flex items-center justify-between mt-4">
                <span className="text-sm font-medium text-blue-600 hover:text-blue-700">
                  Learn More →
                </span>
                <span className="text-xs text-gray-400">Advertisement</span>
              </div>
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
};

export default InFeedAd;
